import { Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { cn } from '@/shared/lib/cn';
import { downloadXlsx, fechaArchivoHoy } from '@/shared/lib/xlsx';
import {
  useVendedores,
  type RangoPreset,
  type VendedorRow,
  type VendedoresDashboard,
} from './ventasApi';
import { fmtARSCompact, fmtDias, fmtInt } from './dashFormat';
import { SERIE_AZUL, useMeasuredWidth } from './ventasCharts';
import { EmptyCard, KpiCard, KpiSkeletonRow, PendientesList } from './dashUi';

const pct = (p: number) => `${(p * 100).toLocaleString('es-AR', { maximumFractionDigits: 1 })}%`;

export function VendedoresView({ rango }: { rango: RangoPreset }) {
  const q = useVendedores(rango);
  if (q.isError) return <EmptyCard>No se pudieron cargar los datos de vendedores.</EmptyCard>;
  if (q.isLoading || !q.data) return <KpiSkeletonRow n={4} />;
  return <VendedoresLoaded data={q.data} />;
}

function VendedoresLoaded({ data }: { data: VendedoresDashboard }) {
  const { totales: t } = data;
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="Cotizaciones realizadas"
          value={fmtInt(t.realizadas)}
          foot={`${fmtInt(t.ganadas)} ganadas · ${fmtInt(t.abiertas)} abiertas`}
        />
        <KpiCard
          label="Conversión"
          value={pct(t.conversion)}
          foot="Ventas sobre cotizaciones"
          accent="#0ca30c"
        />
        <KpiCard
          label="Ratio de cancelación"
          value={pct(t.cancelacion)}
          foot={`${fmtInt(t.anuladas)} anuladas`}
          accent={t.cancelacion > 0.15 ? '#d03b3b' : undefined}
        />
        <KpiCard
          label="Cotizaciones viejas sin cierre"
          value={fmtInt(t.abiertasViejas)}
          foot={`Abiertas hace +${fmtInt(data.umbralDias)} días`}
          accent={t.abiertasViejas > 0 ? '#eda100' : undefined}
        />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Cotizaciones por mes</CardTitle>
        </CardHeader>
        <CardContent>
          <CotizacionesMesChart data={data} />
          <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 rounded-sm bg-muted-foreground/40" /> Cotizadas
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: SERIE_AZUL }} /> Ganadas
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0 pb-3">
          <CardTitle className="text-base">Detalle por vendedor</CardTitle>
          <Button variant="outline" size="sm" onClick={() => exportarVendedores(data)}>
            <Download className="mr-2 h-4 w-4" aria-hidden />
            Descargar XLS
          </Button>
        </CardHeader>
        <CardContent className="px-0">
          <VendedoresTable rows={data.vendedores} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Cotizaciones abiertas más viejas</CardTitle>
        </CardHeader>
        <CardContent>
          <PendientesList
            items={data.abiertasViejas.map((a) => ({
              cliente: `${a.cliente} — ${a.vendedor}`,
              fecha: a.fecha,
              dias: a.dias,
            }))}
            emptyText="Sin cotizaciones viejas sin cerrar."
          />
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        El "tiempo de cierre" mide días entre cotización y <strong>pago total</strong> (no se registra la
        fecha de aceptación, por eso se usa el pago como cierre).
      </p>
    </div>
  );
}

// --- Tabla ---

function VendedoresTable({ rows }: { rows: VendedorRow[] }) {
  const cols: Array<{ label: string; width: string; get: (r: VendedorRow) => string; num?: boolean; emphasis?: boolean; danger?: (r: VendedorRow) => boolean }> = [
    { label: 'Vendedor', width: '19%', get: (r) => r.vendedor },
    { label: 'Cotiz.', width: '9%', get: (r) => fmtInt(r.realizadas), num: true },
    { label: 'Ganadas', width: '9%', get: (r) => fmtInt(r.ganadas), num: true },
    { label: 'Conv.', width: '9%', get: (r) => pct(r.conversion), num: true, emphasis: true },
    { label: 'Cancel.', width: '9%', get: (r) => pct(r.cancelacion), num: true, danger: (r) => r.cancelacion > 0.15 },
    { label: 'Viejas', width: '8%', get: (r) => fmtInt(r.abiertasViejas), num: true, danger: (r) => r.abiertasViejas > 0 },
    { label: 'Ganado', width: '15%', get: (r) => fmtARSCompact(r.montoGanado), num: true, emphasis: true },
    { label: 'Ticket', width: '12%', get: (r) => fmtARSCompact(r.ticketPromedio), num: true },
    { label: 'Cierre', width: '10%', get: (r) => fmtDias(r.tiempoCierre), num: true },
  ];

  return (
    <div className="overflow-x-hidden">
      <table className="w-full table-fixed border-collapse text-sm">
        <colgroup>
          {cols.map((c) => (
            <col key={c.label} style={{ width: c.width }} />
          ))}
        </colgroup>
        <thead className="border-b bg-card">
          <tr>
            {cols.map((c) => (
              <th
                key={c.label}
                className={cn(
                  'px-3 py-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground',
                  c.num ? 'text-right' : 'text-left',
                )}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.vendedor + i} className={cn(i % 2 === 1 && 'bg-muted/30', 'hover:bg-muted/60')}>
              {cols.map((c) => (
                <td
                  key={c.label}
                  className={cn(
                    'overflow-hidden text-ellipsis whitespace-nowrap px-3 py-1.5',
                    c.num ? 'text-right tabular-nums' : 'text-left',
                    c.label === 'Vendedor' || c.emphasis ? 'font-medium text-foreground' : 'text-muted-foreground',
                  )}
                  style={c.danger?.(r) ? { color: '#d03b3b' } : undefined}
                >
                  {c.get(r)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// --- Chart cotizaciones/mes (barras dos-tonos) ---

function CotizacionesMesChart({ data }: { data: VendedoresDashboard }) {
  const { ref, width } = useMeasuredWidth<HTMLDivElement>();
  const serie = data.cotizacionesPorMes;
  const H = 180;
  const PAD = { top: 18, bottom: 24, left: 8, right: 8 };
  const W = Math.max(width, 320);
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;
  const max = Math.max(1, ...serie.map((s) => s.cotizaciones));
  const n = serie.length;
  const slot = innerW / Math.max(1, n);
  const bw = Math.min(34, slot * 0.6);
  const step = Math.ceil(n / Math.max(1, Math.floor(innerW / 56)));

  return (
    <div ref={ref} className="w-full">
      <svg width={W} height={H} role="img" aria-label="Cotizaciones por mes">
        {serie.map((s, i) => {
          const x = PAD.left + slot * i + (slot - bw) / 2;
          const hCot = (s.cotizaciones / max) * innerH;
          const hGan = (s.ganadas / max) * innerH;
          const yCot = PAD.top + innerH - hCot;
          const yGan = PAD.top + innerH - hGan;
          return (
            <g key={s.mes}>
              <title>{`${s.label}: ${s.cotizaciones} cotizadas · ${s.ganadas} ganadas`}</title>
              <rect x={x} y={yCot} width={bw} height={hCot} rx={3} className="fill-muted-foreground" opacity={0.28} />
              {s.ganadas > 0 && <rect x={x} y={yGan} width={bw} height={hGan} rx={3} fill={SERIE_AZUL} />}
              <text x={x + bw / 2} y={yCot - 5} textAnchor="middle" className="fill-foreground" style={{ fontSize: 10, fontWeight: 600 }}>
                {s.cotizaciones > 0 ? fmtInt(s.cotizaciones) : ''}
              </text>
              {(i % step === 0 || i === n - 1) && (
                <text x={x + bw / 2} y={H - 8} textAnchor="middle" className="fill-muted-foreground" style={{ fontSize: 11 }}>
                  {s.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// --- XLS ---

function exportarVendedores(data: VendedoresDashboard) {
  void downloadXlsx({
    filename: `vendedores_${fechaArchivoHoy()}`,
    sheetName: 'Vendedores',
    headers: [
      'Vendedor', 'Cotizaciones', 'Ganadas', 'Conversión %', 'Anuladas', 'Cancelación %',
      'Abiertas', 'Abiertas viejas', 'Monto ganado', 'Ticket promedio', 'Días a cierre',
    ],
    rows: data.vendedores.map((r) => [
      r.vendedor,
      r.realizadas,
      r.ganadas,
      Math.round(r.conversion * 1000) / 10,
      r.anuladas,
      Math.round(r.cancelacion * 1000) / 10,
      r.abiertas,
      r.abiertasViejas,
      Math.round(r.montoGanado),
      Math.round(r.ticketPromedio),
      r.tiempoCierre ?? '',
    ]),
    totals: [
      { label: 'Total cotizaciones', value: data.totales.realizadas },
      { label: 'Total ganadas', value: data.totales.ganadas },
      { label: 'Monto ganado', value: Math.round(data.totales.montoGanado) },
    ],
  });
}
